import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { api } from '../../Api';
import { Store } from '../../Store';
import { getError, otpGatewayLabel as otpProviderLabel } from '../../utils';

/**
 * OTP HISTORY — what was sent, through what, and what it cost.
 *
 * Two views of the same traffic, and the difference between them is the whole
 * reason this screen exists: "Sends" is one row per message that actually left
 * for a gateway, "Codes" is one row per code issued. A code that falls back
 * from WhatsApp to SMS is ONE code and TWO messages, so only the Sends total
 * can be held against a provider's own sent list.
 *
 * Everything above the table narrows both: a date window, a gateway, a
 * category, an outcome, and free text that also searches the failure detail —
 * which is how you find every message that failed the same way.
 */

const CODE_TYPES = ['LOGIN', 'WITHDRAW', 'CONVERT', 'TRANSFER', 'ASSOCIATION_TRANSFER', 'MONEY_REQUEST_APPROVE', 'DELIVERY_REQUEST', 'DELIVERY_HANDOVER', 'TEST'];
// The message log carries kinds nothing else does: the low-balance warning, the
// "a number needs a QR scan" nag, the watchdog's health probe and the topup
// notifications. All spend credit, none of them is a code.
const MESSAGE_TYPES = [...CODE_TYPES, 'ADMIN_LOW_BALANCE', 'ADMIN_WHATSAPP_SCAN', 'ADMIN_HEALTH_PROBE', 'TOPUP'];
const STATUSES = ['pending', 'used', 'cancelled', 'expired'];
// Retired gateways stay in the filter so old rows remain findable.
const PROVIDERS = ['pitech', 'besendswa', 'baileys', 'openwa', 'waha', 'greenweb', 'androidsms'];
const RESULTS = [
  { value: '', label: 'Any outcome' },
  { value: 'delivered', label: 'Delivered (confirmed)' },
  { value: 'sent', label: 'Accepted by gateway' },
  { value: 'unconfirmed', label: 'Unconfirmed (HTTP 5xx / no reply)' },
  { value: 'failed', label: 'Failed' },
  { value: 'timeout', label: 'Timed out' },
  { value: 'skipped', label: 'Skipped (never left)' },
];
const RANGES = [
  { value: 'today', label: 'Today' },
  { value: '7d', label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '', label: 'All time' },
];
const PAGE_SIZES = [25, 50, 100, 200];

const statusColor = (s) => ({
  used: { bg: '#dcfce7', color: '#166534' },
  pending: { bg: '#dbeafe', color: '#1d4ed8' },
  cancelled: { bg: '#fee2e2', color: '#991b1b' },
  expired: { bg: '#f3f4f6', color: '#374151' },
}[s] || { bg: '#f3f4f6', color: '#374151' });

// A message has more outcomes than "worked" and "did not". Accepted means the
// gateway answered 2xx and queued it; delivered means its log later said so.
// A 5xx or a timeout came back with no message id, so it is unconfirmed —
// never "sent". Skipped never left this server.
const resultColor = (r) => {
  const d = r.deliveryStatus;
  if (d === 'delivered') return { label: 'delivered', bg: '#dcfce7', color: '#166534' };
  if (d === 'fail' || d === 'cancel') return { label: `failed (${d})`, bg: '#fee2e2', color: '#991b1b' };
  if (r.ok) return { label: `accepted${r.gatewayStatus || d ? ` · ${d || r.gatewayStatus}` : ''}`, bg: '#dbeafe', color: '#1d4ed8' };
  if (r.skipped) return { label: 'skipped', bg: '#f3f4f6', color: '#374151' };
  if (r.timedOut) return { label: 'no reply', bg: '#fef3c7', color: '#92400e' };
  if (r.mayHaveDelivered) return { label: 'unconfirmed', bg: '#fef3c7', color: '#92400e' };
  return { label: 'failed', bg: '#fee2e2', color: '#991b1b' };
};

// Was the code this message carried ever actually used?
//
// This is the question the delivery status cannot answer. A login code can be
// "delivered" by the gateway and still never reach the handset, and the only
// visible trace is that nobody ever typed it. A row that is delivered and
// unused is the shape a silent delivery failure takes.
const usedColor = (r) => {
  if (r.codeUsed === null || r.codeUsed === undefined) return null; // carried no code
  if (r.codeUsed) return { label: 'used', bg: '#dcfce7', color: '#166534' };
  if (r.codeOutcome === 'expired') return { label: 'expired unused', bg: '#fee2e2', color: '#991b1b' };
  if (r.codeOutcome === 'cancelled') return { label: 'superseded', bg: '#f3f4f6', color: '#374151' };
  return { label: 'not used yet', bg: '#fef3c7', color: '#92400e' };
};

// Which route actually carried it, with the account name only when it adds
// something ("BeSends SMS · BeSends" says nothing twice).
const viaLabel = (label, r) => {
  const base = label(r.provider);
  const account = r.sessionLabel && !/^besends$/i.test(r.sessionLabel.trim()) ? ` · ${r.sessionLabel}` : '';
  return `${base}${r.route ? ` (${r.route})` : ''}${account}`;
};
const httpLabel = (r) => (r.httpStatus ? `HTTP ${r.httpStatus}` : r.timedOut ? 'no reply' : '');

// We asked BeSends for an SMS and it may have carried the message over WhatsApp
// instead, answering exactly as if it had sent an SMS. Once the delivery lookup
// tells us the real route, say so here rather than leaving the log claiming SMS.
const reallyWhatsApp = (r) =>
  String(r.deliveredOver || '').toLowerCase() === 'whatsapp' &&
  String(r.channel || '').toLowerCase() !== 'whatsapp';

const money = (n, dp = 2) => `৳${Number(n || 0).toFixed(dp)}`;
const num = (n) => Number(n || 0).toLocaleString();
const when = (v) => (v ? new Date(v).toLocaleString() : '—');

export default function AdminOtpHistoryScreen() {
  const { state } = useContext(Store);
  const { userInfo } = state;
  const headers = useMemo(() => ({ Authorization: `Bearer ${userInfo?.token}` }), [userInfo]);

  const [view, setView] = useState('messages');
  const [data, setData] = useState({ rows: [], page: 1, pages: 1, total: 0 });
  const [filters, setFilters] = useState({
    type: '', status: '', provider: '', result: '', billable: '', used: '',
    q: '', range: '7d', from: '', to: '',
  });
  // Typed but not yet applied, so a half-typed username does not re-query on
  // every keystroke.
  const [term, setTerm] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [lockdown, setLockdown] = useState(null);
  const [locked, setLocked] = useState([]);
  const [openRow, setOpenRow] = useState(null);
  const [detail, setDetail] = useState(null);
  // Revealed OTP codes, keyed by code row id. Fetched one at a time on click —
  // the list payload never carries plaintext, and a second click hides it again.
  const [codes, setCodes] = useState({});
  const [checking, setChecking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
      if (filters.type) params.set('type', filters.type);
      if (filters.provider) params.set('provider', filters.provider);
      if (filters.q) params.set('q', filters.q);
      if (filters.range) params.set('range', filters.range);
      if (!filters.range && filters.from) params.set('from', filters.from);
      if (!filters.range && filters.to) params.set('to', filters.to);
      if (view === 'codes' && filters.status) params.set('status', filters.status);
      if (view === 'messages' && filters.result) params.set('result', filters.result);
      if (view === 'messages' && filters.billable) params.set('billable', filters.billable);
      if (view === 'messages' && filters.used) params.set('used', filters.used);
      const path = view === 'messages' ? 'otp-messages' : 'otp-history';
      const { data: res } = await axios.get(api + `/api/developer/${path}?${params}`, { headers });
      setData(res);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, headers, view]);

  const loadLockdown = useCallback(async () => {
    try {
      const { data: res } = await axios.get(api + '/api/developer/lockdown', { headers });
      setLockdown(res.lockout);
      setLocked(res.locked || []);
    } catch (err) {
      /* non-critical */
    }
  }, [headers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadLockdown(); }, [loadLockdown]);

  // Ask BeSends for the message's current status and refresh it in place.
  const checkDelivery = async (row) => {
    setChecking(true);
    try {
      const { data: res } = await axios.post(api + `/api/developer/otp-messages/${row._id}/check-delivery`, {}, { headers });
      setOpenRow(res.row);
      setData((d) => (d ? { ...d, rows: (d.rows || []).map((x) => (x._id === res.row._id ? res.row : x)) } : d));
      toast.success(`Delivery status: ${res.row.deliveryStatus || 'unknown'}`);
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setChecking(false);
    }
  };

  // The full picture of one message: the row itself, the code it carried, and
  // the other attempts made for that same code.
  const openMessage = async (row) => {
    setOpenRow(row);
    setDetail(null);
    if (view !== 'messages') return;
    try {
      const { data: res } = await axios.get(api + `/api/developer/otp-message/${row._id}`, { headers });
      setDetail(res);
    } catch (err) {
      /* the row itself is already on screen */
    }
  };

  // Show the code behind ****, so an admin can read it to a customer whose SMS
  // never arrived. Clicking a shown code hides it again.
  const revealCode = async (id) => {
    if (codes[id] !== undefined) {
      setCodes((c) => { const next = { ...c }; delete next[id]; return next; });
      return;
    }
    try {
      const { data: res } = await axios.get(api + `/api/developer/otp-code/${id}`, { headers });
      setCodes((c) => ({ ...c, [id]: res.code || '' }));
      if (!res.code) toast.info('No code stored for this row — it was issued before codes were kept.');
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const setFilter = (patch) => {
    setPage(1);
    setFilters((current) => ({ ...current, ...patch }));
  };

  const saveLockdown = async () => {
    try {
      await axios.put(api + '/api/developer/lockdown', { lockout: lockdown }, { headers });
      toast.success('Lockdown settings saved.');
      loadLockdown();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const unlock = async (row) => {
    try {
      await axios.post(api + '/api/developer/unlock', { userId: row.userId, type: row.type }, { headers });
      toast.success(`${row.username} unlocked.`);
      loadLockdown();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const summary = data.summary || {};
  const daily = data.daily || [];
  const peak = Math.max(1, ...daily.map((d) => d.count || 0));
  const filtersOn = Boolean(
    filters.type || filters.status || filters.provider || filters.result || filters.billable || filters.used || filters.q
  );

  return (
    <div style={st.page}>
      <Helmet><title>OTP History</title></Helmet>

      <div style={st.head}>
        <div>
          <h3 style={{ margin: 0 }}>OTP History</h3>
          <div style={st.muted}>
            Every code issued and every message sent, with what each one cost.
          </div>
        </div>
        <button style={st.btnLight} onClick={load} disabled={loading}>
          <i className="fa fa-refresh" aria-hidden="true" /> {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      {/* Codes vs messages */}
      <div style={st.tabs}>
        <button
          style={view === 'messages' ? st.tabOn : st.tab}
          onClick={() => { setView('messages'); setPage(1); setOpenRow(null); }}
        >
          Sends
        </button>
        <button
          style={view === 'codes' ? st.tabOn : st.tab}
          onClick={() => { setView('codes'); setPage(1); setOpenRow(null); }}
        >
          Codes
        </button>
        <span style={st.muted}>
          {view === 'messages'
            ? 'One row per message sent — compare this total against your SMS provider.'
            : 'One row per code issued. A code can be more than one message.'}
        </span>
      </div>

      {/* Date window */}
      <div style={st.rangeRow}>
        {RANGES.map((r) => (
          <button
            key={r.value || 'all'}
            style={filters.range === r.value ? st.chipOn : st.chip}
            onClick={() => setFilter({ range: r.value })}
          >
            {r.label}
          </button>
        ))}
        <span style={st.muted}>or</span>
        <input
          type="date" style={st.input} value={filters.from}
          onChange={(e) => setFilter({ from: e.target.value, range: '' })}
        />
        <span style={st.muted}>to</span>
        <input
          type="date" style={st.input} value={filters.to}
          onChange={(e) => setFilter({ to: e.target.value, range: '' })}
        />
      </div>

      {/* Statistics */}
      <div style={st.stats}>
        {view === 'messages' ? (
          <>
            <Stat label="Messages" value={num(summary.messages)} hint="In this window" />
            <Stat label="Delivered" value={num(summary.delivered)} hint={`${summary.successRate ?? 0}% of what was tried`} tone="#15803d" />
            <Stat label="Failed" value={num(summary.failed)} hint={`${num(summary.maybe)} unconfirmed · ${num(summary.timedOut)} no reply`} tone="#b91c1c" />
            <Stat label="Skipped" value={num(summary.skipped)} hint="Never left this server" />
            <Stat label="Gateway spend" value={money(summary.cost)} hint={`${num(summary.billed)} billed`} tone="#b45309" />
            <Stat label="Charged to users" value={money(summary.userCharge, 4)} hint="Recovered from members" tone="#1d4ed8" />
          </>
        ) : (
          <>
            <Stat label="Codes" value={num(summary.codes)} hint="In this window" />
            <Stat label="Used" value={num(summary.used)} hint="Verified by the customer" tone="#15803d" />
            <Stat label="Pending" value={num(summary.pending)} hint="Never verified" tone="#1d4ed8" />
            <Stat label="Cancelled" value={num(summary.cancelled)} hint="Superseded or refused" tone="#b91c1c" />
            <Stat label="Expired" value={num(summary.expired)} hint="Ran out of time" />
            <Stat label="Gateway spend" value={money(summary.cost)} hint="Across every attempt" tone="#b45309" />
          </>
        )}
      </div>

      {/* Per gateway — the table that answers "which one is carrying this" */}
      {view === 'messages' && (data.byProvider || []).length > 0 && (
        <div style={st.card}>
          <div style={st.cardHead}>By gateway</div>
          <div style={st.tableWrap}>
            <table style={st.table}>
              <thead>
                <tr>
                  <th style={st.th}>Gateway</th><th style={st.th}>Messages</th><th style={st.th}>Delivered</th>
                  <th style={st.th}>Failed</th><th style={st.th}>Skipped</th><th style={st.th}>Success</th>
                  <th style={st.th}>Spend</th><th style={st.th}>Share</th>
                </tr>
              </thead>
              <tbody>
                {data.byProvider.map((p) => (
                  <tr
                    key={p._id}
                    style={{ cursor: 'pointer', background: filters.provider === p._id ? '#eff6ff' : undefined }}
                    onClick={() => setFilter({ provider: filters.provider === p._id ? '' : p._id })}
                    title="Click to filter by this gateway"
                  >
                    <td style={st.td}><strong>{otpProviderLabel(p._id)}</strong></td>
                    <td style={st.td}>{num(p.count)}</td>
                    <td style={st.td}>{num(p.delivered)}</td>
                    <td style={st.td}>{num(p.failed)}</td>
                    <td style={st.td}>{num(p.skipped)}</td>
                    <td style={st.td}>
                      <span style={{ ...st.pill, ...(p.successRate >= 90
                        ? { background: '#dcfce7', color: '#166534' }
                        : p.successRate >= 60
                        ? { background: '#fef3c7', color: '#92400e' }
                        : { background: '#fee2e2', color: '#991b1b' }) }}>
                        {p.successRate}%
                      </span>
                    </td>
                    <td style={st.td}>{money(p.cost)}</td>
                    <td style={{ ...st.td, minWidth: 120 }}>
                      <div style={st.barTrack}>
                        <div style={{ ...st.barFill, width: `${Math.round((p.count / Math.max(1, summary.messages)) * 100)}%` }} />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per day. Deliberately plain divs — a chart library for six bars is a
          dependency nobody needs. */}
      {daily.length > 1 && (
        <div style={st.card}>
          <div style={st.cardHead}>Per day</div>
          <div style={st.chart}>
            {daily.map((d) => (
              <div key={d._id} style={st.chartCol} title={`${d._id}: ${d.count} ${view === 'messages' ? 'messages' : 'codes'}`}>
                <div style={st.chartBarWrap}>
                  <div style={{ ...st.chartBar, height: `${Math.round((d.count / peak) * 100)}%` }}>
                    <div
                      style={{
                        ...st.chartBarOk,
                        height: `${Math.round(((view === 'messages' ? d.delivered : d.used) / Math.max(1, d.count)) * 100)}%`,
                      }}
                    />
                  </div>
                </div>
                <span style={st.chartLabel}>{d._id.slice(5)}</span>
              </div>
            ))}
          </div>
          <div style={st.muted}>
            Height is everything sent that day; the green part is what
            {view === 'messages' ? ' was delivered.' : ' the customer actually used.'}
          </div>
        </div>
      )}

      {/* Filters */}
      <div style={st.filters}>
        <input
          style={{ ...st.input, flex: '1 1 220px' }}
          placeholder={view === 'messages' ? 'Search username, phone, or failure detail' : 'Search username or phone'}
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => (e.key === 'Enter' ? setFilter({ q: term }) : null)}
        />
        <button style={st.btn} onClick={() => setFilter({ q: term })}>Search</button>

        <select style={st.input} value={filters.type} onChange={(e) => setFilter({ type: e.target.value })}>
          <option value="">All categories</option>
          {(view === 'messages' ? MESSAGE_TYPES : CODE_TYPES).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>

        <select style={st.input} value={filters.provider} onChange={(e) => setFilter({ provider: e.target.value })}>
          <option value="">All gateways</option>
          {PROVIDERS.map((p) => <option key={p} value={p}>{otpProviderLabel(p)}</option>)}
        </select>

        {view === 'messages' ? (
          <>
            <select style={st.input} value={filters.result} onChange={(e) => setFilter({ result: e.target.value })}>
              {RESULTS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            
            <select style={st.input} value={filters.used} onChange={(e) => setFilter({ used: e.target.value })}>
              <option value="">Used or not</option>
              <option value="true">Code was used</option>
              <option value="false">Code never used</option>
            </select>
          </>
        ) : (
          <select style={st.input} value={filters.status} onChange={(e) => setFilter({ status: e.target.value })}>
            <option value="">All statuses</option>
            {STATUSES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        )}

        <select style={st.input} value={pageSize} onChange={(e) => { setPage(1); setPageSize(Number(e.target.value)); }}>
          {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
        </select>

        {filtersOn && (
          <button
            style={st.btnLight}
            onClick={() => {
              setTerm('');
              setFilter({ type: '', status: '', provider: '', result: '', billable: '', used: '', q: '' });
            }}
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Message table */}
      {view === 'messages' && (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>When</th><th style={st.th}>To</th><th style={st.th}>Category</th>
                <th style={st.th}>Via</th><th style={st.th}>Result</th><th style={st.th}>OTP used</th>
                <th style={st.th}>Detail</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={st.td} colSpan={10}>Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td style={st.td} colSpan={10}>No messages match this view.</td></tr>
              ) : data.rows.map((r) => {
                const rc = resultColor(r);
                return (
                  <tr
                    key={r._id}
                    style={{ cursor: 'pointer', background: openRow?._id === r._id ? '#eff6ff' : undefined }}
                    onClick={() => openMessage(r)}
                  >
                    <td style={st.td}>{when(r.createdAt)}</td>
                    <td style={st.td}>{r.username || r.phone || '—'}</td>
                    <td style={st.td}>{r.type}</td>
                    <td style={st.td}>
                      {viaLabel(otpProviderLabel, r)}
                      {reallyWhatsApp(r) ? (
                        <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                          sent over WhatsApp{r.deliveredFrom ? ` · ${r.deliveredFrom}` : ''}
                        </div>
                      ) : null}
                    </td>
                    <td style={st.td}>
                      <span style={{ ...st.pill, background: rc.bg, color: rc.color }}>{rc.label}</span>
                      {httpLabel(r) ? <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{httpLabel(r)}</div> : null}
                    </td>
                    <td style={st.td}>
                      {(() => {
                        const uc = usedColor(r);
                        if (!uc) return <span style={{ color: '#9ca3af' }}>—</span>;
                        return (
                          <>
                            <span style={{ ...st.pill, background: uc.bg, color: uc.color }}>{uc.label}</span>
                            {r.codeSmsFollowUp === 'sent' ? (
                              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>SMS re-sent</div>
                            ) : null}
                          </>
                        );
                      })()}
                    </td>
                    <td style={{ ...st.td, maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={r.detail}>{r.detail || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Code table */}
      {view === 'codes' && (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>When</th><th style={st.th}>User</th><th style={st.th}>Category</th>
                <th style={st.th}>OTP</th><th style={st.th}>Status</th><th style={st.th}>Try</th>
                <th style={st.th}>Admin Cost</th><th style={st.th}>User Charge</th><th style={st.th}>Channels</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td style={st.td} colSpan={9}>Loading…</td></tr>
              ) : data.rows.length === 0 ? (
                <tr><td style={st.td} colSpan={9}>No codes match this view.</td></tr>
              ) : data.rows.map((r) => {
                const sc = statusColor(r.status);
                return (
                  <tr
                    key={r._id}
                    style={{ cursor: 'pointer', background: openRow?._id === r._id ? '#eff6ff' : undefined }}
                    onClick={() => { setOpenRow(r); setDetail(null); }}
                  >
                    <td style={st.td}>{when(r.createdAt)}</td>
                    <td style={st.td}>{r.username || r.phone || '—'}</td>
                    <td style={st.td}>{r.type}</td>
                    <td style={st.td} onClick={(e) => e.stopPropagation()}>
                      <button
                        style={codes[r._id] !== undefined ? st.codeShown : st.codeHidden}
                        onClick={() => revealCode(r._id)}
                        title={codes[r._id] !== undefined ? 'Click to hide' : 'Click to view the code'}
                      >
                        {codes[r._id] !== undefined ? (codes[r._id] || 'not stored') : '****'}
                      </button>
                    </td>
                    <td style={st.td}><span style={{ ...st.pill, background: sc.bg, color: sc.color }}>{r.status}</span></td>
                    <td style={st.td}>{r.attempts}/{r.maxAttempts}</td>
                    <td style={st.td}>{money(r.cost)}</td>
                    <td style={st.td}>{r.userCharge ? money(r.userCharge) : '—'}</td>
                    <td style={st.td}>
                      {(r.channels || [])
                        .map((c) => `${otpProviderLabel(c.provider)}:${c.ok ? '✓' : c.mayHaveDelivered ? '?' : '✗'}`)
                        .join(' ') || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      <div style={st.pager}>
        <button style={st.btnLight} disabled={page <= 1} onClick={() => setPage(1)}>« First</button>
        <button style={st.btnLight} disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>‹ Prev</button>
        <span style={{ margin: '0 10px', fontSize: 13 }}>
          Page <strong>{data.page}</strong> of {data.pages} · showing {data.rows.length} of{' '}
          <strong>{num(data.total)}</strong>
        </span>
        <button style={st.btnLight} disabled={page >= data.pages} onClick={() => setPage((p) => p + 1)}>Next ›</button>
        <button style={st.btnLight} disabled={page >= data.pages} onClick={() => setPage(data.pages)}>Last »</button>
      </div>

      {/* Lockdown panel. Below the log now: it is a setting, and the log is what
          this page is opened for. */}
      <div style={st.card}>
        <div style={st.cardHead}>OTP lockdown</div>
        {lockdown && (
          <div style={st.lockRow}>
            <label style={st.check}>
              <input type="checkbox" checked={lockdown.enabled} onChange={(e) => setLockdown({ ...lockdown, enabled: e.target.checked })} />
              <span>Enable lockdown</span>
            </label>
            <div style={st.inlineField}>
              <span>Lock after</span>
              <input style={st.num} type="number" min="1" value={lockdown.threshold} onChange={(e) => setLockdown({ ...lockdown, threshold: e.target.value })} />
              <span>failed tries, for</span>
              <input style={st.num} type="number" min="0" step="0.5" value={lockdown.hours} onChange={(e) => setLockdown({ ...lockdown, hours: e.target.value })} />
              <span>hour(s)</span>
            </div>
            <button style={st.btn} onClick={saveLockdown}>Save</button>
          </div>
        )}
        <div style={{ marginTop: 12 }}>
          <strong style={{ fontSize: 13 }}>Currently locked ({locked.length})</strong>
          {locked.length === 0 ? (
            <p style={st.muted}>No users are locked out.</p>
          ) : (
            <div style={st.tableWrap}>
              <table style={st.table}>
                <thead><tr><th style={st.th}>User</th><th style={st.th}>Type</th><th style={st.th}>Unlocks in</th><th style={st.th}></th></tr></thead>
                <tbody>
                  {locked.map((l) => (
                    <tr key={l._id}>
                      <td style={st.td}>{l.username}</td>
                      <td style={st.td}>{l.type}</td>
                      <td style={st.td}>~{l.minutesLeft} min</td>
                      <td style={st.td}><button style={st.unlockBtn} onClick={() => unlock(l)}>Unlock</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* One row, everything about it */}
      {openRow && (
        <div style={st.scrim} onClick={() => setOpenRow(null)}>
          <div style={st.drawer} onClick={(e) => e.stopPropagation()}>
            <div style={st.drawerHead}>
              <strong>{view === 'messages' ? 'Message' : 'Code'} detail</strong>
              <button style={st.x} onClick={() => setOpenRow(null)}>×</button>
            </div>
            <div style={st.drawerBody}>
              {view === 'messages' ? (
                <>
                  <Row k="Sent" v={when(openRow.createdAt)} />
                  <Row k="To" v={openRow.phone || '—'} />
                  <Row k="User" v={openRow.username || '— (no account)'} />
                  <Row k="Category" v={openRow.type} />
                  <Row k="Channel" v={viaLabel(otpProviderLabel, openRow)} />
                  <Row k="Outcome" v={resultColor(openRow).label} />
                  <Row k="Gateway said" v={openRow.detail || '—'} wrap />
                  <div style={st.secTitle}>API response</div>
                  <Row k="HTTP status" v={httpLabel(openRow) || '—'} />
                  <Row k="BeSends message id" v={openRow.sessionId || 'none returned'} />
                  <Row k="Status on send" v={openRow.gatewayStatus || '—'} />
                  <Row
                    k="Delivery status"
                    v={openRow.deliveryStatus
                      ? `${openRow.deliveryStatus}${openRow.deliveryCheckedAt ? ` (checked ${when(openRow.deliveryCheckedAt)})` : ''}`
                      : openRow.sessionId ? 'not checked yet' : 'cannot be checked — no message id'}
                  />
                  {openRow.sessionId && ['pitech', 'besendswa'].includes(openRow.provider) ? (
                    <button style={st.unlockBtn} disabled={checking} onClick={() => checkDelivery(openRow)}>
                      {checking ? 'Checking…' : 'Check delivery now'}
                    </button>
                  ) : null}
                  {openRow.response ? (
                    <>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Raw send response</div>
                      <pre style={st.raw}>{openRow.response}</pre>
                    </>
                  ) : null}
                  {openRow.deliveryResponse ? (
                    <>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>Raw delivery lookup</div>
                      <pre style={st.raw}>{openRow.deliveryResponse}</pre>
                    </>
                  ) : null}
                  <div style={st.secTitle}>Money</div>
                  <Row k="Billed" v={openRow.billable ? 'Yes' : 'No — nothing was spent'} />
                  <Row k="Cost to us" v={money(openRow.cost)} />
                  <Row k="Charged to user" v={openRow.userCharge ? `${money(openRow.userCharge, 4)} from ${openRow.userChargedFrom || 'their balance'}` : 'Nothing'} />
                  {openRow.userBalanceAfter !== null && openRow.userBalanceAfter !== undefined ? (
                    <Row k="Their balance after" v={num(openRow.userBalanceAfter)} />
                  ) : null}

                  {detail?.code ? (
                    <>
                      <div style={st.secTitle}>The code it carried</div>
                      <Row k="Status" v={detail.code.status} />
                      <Row
                        k="OTP code"
                        v={
                          <button
                            style={codes[detail.code._id] !== undefined ? st.codeShown : st.codeHidden}
                            onClick={() => revealCode(detail.code._id)}
                            title={codes[detail.code._id] !== undefined ? 'Click to hide' : 'Click to view the code'}
                          >
                            {codes[detail.code._id] !== undefined ? (codes[detail.code._id] || 'not stored') : '****'}
                          </button>
                        }
                      />
                      <Row k="Tries" v={`${detail.code.attempts}/${detail.code.maxAttempts}`} />
                      <Row k="Expires" v={when(detail.code.expiresAt)} />
                    </>
                  ) : null}

                  {detail?.siblings?.length ? (
                    <>
                      <div style={st.secTitle}>Other attempts for this code</div>
                      {detail.siblings.map((sib) => {
                        const rc = resultColor(sib);
                        return (
                          <div key={sib._id} style={st.trail}>
                            <span style={{ ...st.pill, background: rc.bg, color: rc.color }}>{rc.label}</span>
                            <strong style={{ marginLeft: 6 }}>{otpProviderLabel(sib.provider)}</strong>
                            <div style={st.muted}>{when(sib.createdAt)} · {sib.detail || 'no detail'}</div>
                          </div>
                        );
                      })}
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  <Row k="Issued" v={when(openRow.createdAt)} />
                  <Row k="User" v={openRow.username || '—'} />
                  <Row k="Phone" v={openRow.phone || '—'} />
                  <Row k="Category" v={openRow.type} />
                  <Row k="Status" v={openRow.status} />
                  <Row
                    k="OTP code"
                    v={
                      <button
                        style={codes[openRow._id] !== undefined ? st.codeShown : st.codeHidden}
                        onClick={() => revealCode(openRow._id)}
                        title={codes[openRow._id] !== undefined ? 'Click to hide' : 'Click to view the code'}
                      >
                        {codes[openRow._id] !== undefined ? (codes[openRow._id] || 'not stored') : '****'}
                      </button>
                    }
                  />
                  <Row k="Tries" v={`${openRow.attempts}/${openRow.maxAttempts}`} />
                  <Row k="Expires" v={when(openRow.expiresAt)} />
                  <Row k="Cost to us" v={money(openRow.cost)} />
                  <Row k="Charged to user" v={openRow.userCharge ? money(openRow.userCharge, 4) : 'Nothing'} />
                  <div style={st.secTitle}>Every attempt made for this code</div>
                  {(openRow.channels || []).length === 0 ? (
                    <div style={st.muted}>Nothing was attempted.</div>
                  ) : openRow.channels.map((c, i) => {
                    const rc = resultColor(c);
                    return (
                      <div key={i} style={st.trail}>
                        <span style={{ ...st.pill, background: rc.bg, color: rc.color }}>{rc.label}</span>
                        <strong style={{ marginLeft: 6 }}>
                          {otpProviderLabel(c.provider)}{c.sessionLabel ? ` · ${c.sessionLabel}` : ''}
                        </strong>
                        <div style={st.muted}>{when(c.at)} · {c.detail || 'no detail'}</div>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, hint, tone }) {
  return (
    <div style={st.stat}>
      <span style={st.statLabel}>{label}</span>
      <strong style={{ ...st.statValue, color: tone || '#111827' }}>{value}</strong>
      <span style={st.statHint}>{hint}</span>
    </div>
  );
}

function Row({ k, v, wrap }) {
  return (
    <div style={st.kv}>
      <span>{k}</span>
      <b style={wrap ? { textAlign: 'right', wordBreak: 'break-word' } : { textAlign: 'right' }}>{v}</b>
    </div>
  );
}

const st = {
  page: { width: '87%', float: 'right', padding: '16px 20px 60px', boxSizing: 'border-box' },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' },
  muted: { color: '#6b7280', fontSize: 12.5 },
  card: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 14, margin: '12px 0' },
  cardHead: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: '#6b7280', marginBottom: 8 },

  tabs: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 16 },
  tab: { background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: 8, padding: '7px 16px', fontWeight: 600, cursor: 'pointer' },
  tabOn: { background: '#111827', color: '#fff', border: '1px solid #111827', borderRadius: 8, padding: '7px 16px', fontWeight: 600, cursor: 'pointer' },

  rangeRow: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' },
  chip: { background: '#fff', border: '1px solid #d1d5db', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#374151' },
  chipOn: { background: '#2563eb', border: '1px solid #2563eb', borderRadius: 999, padding: '6px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer', color: '#fff' },

  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10, margin: '12px 0' },
  stat: { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '11px 13px', display: 'flex', flexDirection: 'column', gap: 1 },
  statLabel: { fontSize: 11, textTransform: 'uppercase', letterSpacing: '.04em', color: '#6b7280', fontWeight: 700 },
  statValue: { fontSize: 21 },
  statHint: { fontSize: 11.5, color: '#6b7280' },

  barTrack: { background: '#f1f5f9', borderRadius: 999, height: 8, width: '100%', overflow: 'hidden' },
  barFill: { background: '#2563eb', height: '100%' },

  chart: { display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, overflowX: 'auto', paddingBottom: 4 },
  chartCol: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, minWidth: 26, flex: '1 0 auto' },
  chartBarWrap: { height: 92, display: 'flex', alignItems: 'flex-end', width: '100%' },
  chartBar: { width: '100%', background: '#dbeafe', borderRadius: '4px 4px 0 0', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', minHeight: 2 },
  chartBarOk: { width: '100%', background: '#16a34a', borderRadius: '4px 4px 0 0' },
  chartLabel: { fontSize: 9.5, color: '#6b7280', whiteSpace: 'nowrap' },

  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', margin: '12px 0' },
  input: { border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 10px', fontSize: 14, boxSizing: 'border-box' },
  btn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' },
  btnLight: { background: '#f3f4f6', color: '#111827', border: '1px solid #d1d5db', borderRadius: 8, padding: '8px 14px', fontWeight: 600, cursor: 'pointer' },
  unlockBtn: { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontWeight: 600 },

  tableWrap: { overflowX: 'auto', background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12 },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: 760 },
  th: { textAlign: 'left', padding: '9px 10px', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: 11, textTransform: 'uppercase', color: '#6b7280' },
  td: { padding: '9px 10px', borderBottom: '1px solid #f3f4f6', fontSize: 13 },
  pill: { borderRadius: 999, padding: '2px 9px', fontSize: 12, fontWeight: 700, display: 'inline-block' },
  codeHidden: { background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontWeight: 700, letterSpacing: 2, color: '#6b7280' },
  codeShown: { background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 6, padding: '2px 10px', cursor: 'pointer', fontWeight: 800, letterSpacing: 2, color: '#1d4ed8', fontFamily: 'monospace' },
  pager: { marginTop: 14, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' },

  lockRow: { display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' },
  check: { display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600 },
  inlineField: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 14 },
  num: { width: 64, border: '1px solid #d1d5db', borderRadius: 6, padding: '5px 8px' },

  scrim: { position: 'fixed', inset: 0, background: 'rgba(15,23,42,.45)', zIndex: 60, display: 'flex', justifyContent: 'flex-end' },
  drawer: { background: '#fff', width: 'min(460px, 100%)', height: '100%', display: 'flex', flexDirection: 'column', boxShadow: '-8px 0 30px rgba(0,0,0,.2)' },
  drawerHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #e5e7eb' },
  drawerBody: { padding: 16, overflowY: 'auto' },
  x: { border: 0, background: 'none', fontSize: 24, lineHeight: 1, cursor: 'pointer', color: '#6b7280' },
  secTitle: { marginTop: 14, marginBottom: 4, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: '#6b7280' },
  raw: { background: '#f3f4f6', padding: 8, borderRadius: 6, fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all', maxHeight: 200, overflow: 'auto', margin: '4px 0' },
  kv: { display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, borderBottom: '1px solid #f3f4f6', padding: '6px 0' },
  trail: { borderLeft: '2px solid #e5e7eb', padding: '4px 0 8px 10px', marginTop: 6, fontSize: 12.5 },
};
